import CloseOutlined from "@mui/icons-material/CloseOutlined"
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Drawer,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Snackbar,
	Typography,
} from "@mui/material"
import { AxiosError, AxiosResponse } from "axios"
import { ChangeEvent, FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { ReactComponent as SettingSvg } from "../../assets/img/settings.svg"
import { AppDispatch, RootState } from "../../store"
import { AuthResponse, updateProfile } from "../../store/actions/auth"
import { ProfileData } from "../../store/actions/user"
import { isValidPhoneNumber } from "../../utils/sepa-phone-validator"
import { supportedLanguage } from "../../utils/supported-language"
import InputPassword from "../common/input-password"
import SepaPhoneNumberInput from "../plick/drawer/sepaPhoneNumberInput"
import RoleBanner from "../role/banner"
import EditableAvatar from "./editable-avatar"

interface IProfileProps {
	handleClose: () => void
	isDrawerOpen: boolean
}

export type FormErrors = {
	[key: string]: string | undefined
}

const Profile: FC<IProfileProps> = ({ handleClose, isDrawerOpen }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()
	const currentUser: AuthResponse | undefined = useSelector(
		(state: RootState) => state.auth?.user
	)

	const [errors, setErrors] = useState(undefined)
	const [isSaved, setIsSaved] = useState<boolean>(false)
	const [alertMessage, setAlertMessage] = useState<string | undefined>(
		undefined
	)
	const [errorsList, setErrorsList] = useState<FormErrors | undefined>(
		undefined
	)
	const [snackBarType, setSnackBarType] = useState<string | undefined>(
		undefined
	)
	const [formData, setFormData] = useState<ProfileData>({
		avatar: undefined,
		phone: undefined,
		currentPassword: undefined,
		newPassword: undefined,
		repeatPassword: undefined,
		language: "it",
	})
	const [isSavingForm, setIsSavingForm] = useState<boolean>(false)

	const [updateFormData, setUpdateFormData] = useState<ProfileData | undefined>(
		undefined
	)

	useEffect(() => {
		setFormData({
			avatar: currentUser?.avatar || undefined,
			phone: currentUser?.phone || undefined,
			currentPassword: undefined,
			newPassword: undefined,
			repeatPassword: undefined,
			language: currentUser?.language || undefined,
		})
	}, [currentUser])

	/*
	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target

		if (formData?.[name] !== value) {
			setUpdateFormData((prevData) => ({
				...prevData,
				[name]: value,
			}))
		}

		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}))
	}
*/
	const handleChange =
		(field: string) => (event: ChangeEvent<HTMLInputElement> | string) => {
			let newValue = event?.target?.value ?? event
			let newErrors: FormErrors | undefined = { ...errorsList }
			// VALIDATION
			switch (field) {
				case "currentPassword":
					if (formData?.newPassword && !newValue) {
						newErrors.currentPassword = "errors.notPresent"
						setErrorsList(newErrors)
						break
					}

					if (formData?.currentPassword !== newValue) {
						setUpdateFormData((prevData) => ({
							...prevData,
							[field]: newValue,
						}))

						setFormData((prevData) => ({
							...prevData,
							[field]: newValue,
						}))
					}

					break
				case "newPassword":
					const regexpLowerUpperCase = /^(?=.*[a-z])(?=.*[A-Z]).+$/
					const regexpSpecialChar = /[^\d\sA-Za-z]/
					const regexNumber = /\d/
					const passwordToCheck: string = newValue ?? ""

					if (!passwordToCheck || passwordToCheck.length == 0) {
						const { ["newPassword"]: _, ...remainingErrors } = newErrors
						newErrors = remainingErrors
						break
					}
					if (passwordToCheck.length < 8) {
						newErrors.newPassword = "errors.ruleMinimumChar"
						setErrorsList(newErrors)
						break
					} else {
						const { ["newPassword"]: _, ...remainingErrors } = newErrors
						newErrors = remainingErrors
					}

					if (!regexpLowerUpperCase.test(passwordToCheck)) {
						newErrors.newPassword = "errors.ruleLowerUpper"
						setErrorsList(newErrors)
						break
					}

					if (!regexpSpecialChar.test(passwordToCheck)) {
						newErrors.newPassword = "errors.ruleSpecialChar"
						setErrorsList(newErrors)
						break
					}

					if (!regexNumber.test(passwordToCheck)) {
						newErrors.newPassword = "errors.ruleNumber"
						setErrorsList(newErrors)
						break
					}

					if (formData?.newPassword !== newValue) {
						setUpdateFormData((prevData) => ({
							...prevData,
							[field]: newValue,
						}))

						setFormData((prevData) => ({
							...prevData,
							[field]: newValue,
						}))
					}

					break
				case "repeatPassword":
					if (formData?.newPassword && !newValue) {
						newErrors.repeatPassword = "errors.notPresent"
						setErrorsList(newErrors)
						break
					}

					if (newValue !== formData?.newPassword) {
						newErrors.repeatPassword = "errors.notEqual"
						setErrorsList(newErrors)
						break
					} else {
						const { repeatPassword, ...updatedNewErrors } = newErrors
						setErrorsList(updatedNewErrors)
					}

					if (formData?.repeatPassword !== newValue) {
						setUpdateFormData((prevData) => ({
							...prevData,
							[field]: newValue,
						}))

						setFormData((prevData) => ({
							...prevData,
							[field]: newValue,
						}))
					}

					break
				case "phone":
					const mailRegexp = new RegExp("^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$")
					const numberNoSpace = newValue.replace(/\s/g, "")
					const isValidNumber = isValidPhoneNumber(numberNoSpace)
					if (!isValidNumber && !mailRegexp.test(newValue)) {
						newErrors.phone = "errors.formatWrong"
					} else {
						const { ["phone"]: _, ...remainingErrors } = newErrors
						newErrors = remainingErrors
					}
					setErrorsList(newErrors)

					setUpdateFormData((prevData) => ({
						...prevData,
						[field]: numberNoSpace,
					}))

					break
			}
		}

	const handleSaveForm = () => {
		setIsSavingForm(true)
		dispatch(
			updateProfile({ profileData: updateFormData, idUser: currentUser?.id })
		)
			.then((result: AxiosResponse) => {
				setIsSavingForm(false)
				if (result?.meta?.requestStatus === "rejected") {
					setSnackBarType("error")
					if (result?.payload?.status === 403) {
						setAlertMessage(
							t("unauthorized.error.alertMessage", { ns: "common" })
						)
					} else if (result?.payload?.status === 400) {
						const errorSplit = result?.payload?.data?.message.split(".")

						const backendErrors = result.payload?.data?.errors?.reduce(
							(acc, err) => {
								acc[err.field] = err.defaultMessage
								return acc
							},
							{}
						)

						setErrors((prevData) => ({
							...prevData,
							...backendErrors,
							[errorSplit[1]]: result?.payload?.data?.message,
						}))
					}
				} else {
					setSnackBarType("success")
					setAlertMessage(t("form.modified", { ns: "userMenu" }))
					setIsSaved(true)
				}
			})
			.catch((error: AxiosError) => {
				setSnackBarType("error")
				if (error.response && error.response.status === 400) {
					const backendErrors = error.response.data.reduce((acc, err) => {
						acc[err.field] = err.defaultMessage
						return acc
					}, {})
					setErrors(backendErrors)
				} else if (error.response && error.response.status === 404) {
					setAlertMessage(
						t("unauthorized.error.alertMessage", { ns: "common" })
					)
				}
			})
	}

	return (
		<>
			<Drawer
				anchor="right"
				open={isDrawerOpen}
				onClose={() => {
					setIsSaved(false)
					handleClose()
				}}
			>
				<Grid p={2} container justifyContent={"space-between"}>
					<Grid item flexGrow={1}>
						<Typography component="span" variant="h6">
							{t("profile.title", { ns: "userMenu" })}
						</Typography>
					</Grid>
					<Grid item>
						<IconButton
							onClick={() => {
								setIsSaved(false)
								handleClose()
							}}
							sx={{ width: "30px", height: "30px" }}
						>
							<CloseOutlined />
						</IconButton>
					</Grid>
				</Grid>
				{isSaved ? (
					<Paper square elevation={0} sx={{ p: 4 }}>
						<Grid container>
							<Grid item>
								<SettingSvg width={"200px"} height={"200px"} />
							</Grid>
							<Grid item sx={{ alignContent: "center" }}>
								<Typography sx={{ fontWeight: "bold" }} component="div">
									{t("form.modified", {
										ns: "userMenu",
									})}
								</Typography>
							</Grid>
						</Grid>
					</Paper>
				) : (
					<Box p={2} sx={{ minWidth: "360px", width: "45vw" }}>
						<Box
							component="form"
							onSubmit={() => {}}
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 3,
								margin: "5px auto",
							}}
						>
							<Box
								sx={{
									display: "flex",
									gap: "16px",
									alignItems: "center",
									justifyContent: "left",
								}}
							>
								<EditableAvatar
									currentImage={currentUser?.avatar}
									size={180}
									onChange={(file: File) => {
										setUpdateFormData((prev) => {
											return {
												...prev,
												avatar: file,
											} as ProfileData
										})

										setFormData((prev) => ({
											...prev,
											avatar: file, // oppure field se dinamico
										}))
									}}
								/>
								<div>
									<Typography variant="h6">{currentUser?.fullName}</Typography>
									<br />
									{currentUser?.email}
									<br />
									<RoleBanner role={currentUser?.roles[0].role} />
								</div>
							</Box>
							<InputPassword
								id="currentPassword"
								label={t("form.password.current", { ns: "userMenu" })}
								variant="outlined"
								value={formData?.currentPassword}
								onChange={handleChange("currentPassword")}
								fullWidth={true}
								error={Boolean(errorsList && errorsList?.currentPassword)} // Mostra errore solo se presente
								helperText={
									errorsList && errorsList?.currentPassword
										? `${t("errors.error", { ns: "userMenu" })}: ${t(errorsList?.currentPassword, { ns: "userMenu" })}`
										: ""
								}
							/>

							<InputPassword
								id="newPassword"
								label={t("form.password.new", { ns: "userMenu" })}
								variant="outlined"
								value={formData?.newPassword}
								onChange={handleChange("newPassword")}
								fullWidth={true}
								error={Boolean(errorsList && errorsList?.newPassword)} // Mostra errore solo se presente
								helperText={
									errorsList && errorsList?.currentPassword
										? `${t("errors.error", { ns: "userMenu" })}: ${t(errorsList?.currentPassword, { ns: "userMenu" })}`
										: ""
								}
							/>

							<InputPassword
								id="repeatPassword"
								label={t("form.password.repeat", { ns: "userMenu" })}
								variant="outlined"
								value={formData?.repeatPassword}
								onChange={handleChange("repeatPassword")}
								fullWidth={true}
								error={Boolean(errorsList && errorsList?.repeatPassword)} // Mostra errore solo se presente
								helperText={
									errorsList && errorsList?.repeatPassword
										? `${t("errors.error", { ns: "userMenu" })}: ${t(errorsList?.repeatPassword, { ns: "userMenu" })}`
										: ""
								}
							/>

							<SepaPhoneNumberInput
								label={t("form.phone", {
									ns: "userMenu",
								})} // Etichetta leggibile
								errorsList={errorsList}
								field={"phone"}
								setErrorsList={setErrorsList}
								handleChange={handleChange}
								disabled={false}
								phoneValue={formData?.phone}
							/>
							<FormControl fullWidth>
								<InputLabel id="language-selector-label">
									{t("form.language", { ns: "userMenu" })}
								</InputLabel>
								<Select
									labelId="language-selector-label"
									id="language-selector"
									value={formData?.language}
									label={t("form.language", { ns: "userMenu" })}
									autoWidth={true}
								>
									{supportedLanguage.map(
										(lang: { value: string; label: string }) => {
											return (
												<MenuItem
													key={`lang_${lang.value}`}
													onClick={() => {
														setFormData((prevData) => ({
															...prevData,
															language: lang.value,
														}))

														setUpdateFormData((prev) => {
															return {
																...prev,
																language: lang.value,
															} as ProfileData
														})
													}}
													value={lang.value}
												>
													{lang.label.toUpperCase()}
												</MenuItem>
											)
										}
									)}
								</Select>
							</FormControl>
							<Box sx={{ display: "flex", alignItem: "center" }}>
								{isSavingForm ? (
									<CircularProgress size="50px" />
								) : (
									<Button
										variant="contained"
										onClick={handleSaveForm}
										sx={{ margin: "16px 0" }}
									>
										{t("form.buttons.save", { ns: "userMenu" })}
									</Button>
								)}
							</Box>
						</Box>
					</Box>
				)}
			</Drawer>
			{!!snackBarType && (
				<Snackbar
					sx={{ width: "calc(200% - 50px)", zIndex: 9999 }}
					anchorOrigin={{ vertical: "top", horizontal: "left" }}
					open={!!snackBarType}
					autoHideDuration={30000}
					onClose={() => setSnackBarType(undefined)}
				>
					<Alert
						onClose={() => setSnackBarType(undefined)}
						severity={snackBarType}
						variant="filled"
						sx={{ width: "200%", zIndex: 9999 }}
					>
						{alertMessage}
					</Alert>
				</Snackbar>
			)}
		</>
	)
}

export default Profile
